/**
 * The order of the home sliders in the dashboard (the API keeps one order for
 * every slider, in `sort_order`).
 *
 * A slider belongs to one city or to all of them, so a customer sees only a
 * part of the full list: the sliders of his city and the global ones. Moving a
 * slider one step in the FULL list could just pass a slider of another city,
 * and change nothing that anybody sees. So a move is made in the list that is
 * ON SCREEN, and written into the full order by swapping the two sliders.
 */

/**
 * The sliders that a customer of one city sees, in their order.
 *
 * @param {Array<{id: number, cityId?: number|null}>} sliders - the full list, in order
 * @param {string|number} cityId - "" for every slider
 * @returns {Array<object>}
 */
export const slidersOfCity = (sliders, cityId) =>
    cityId === "" || cityId === null || cityId === undefined
        ? sliders
        : sliders.filter((slider) => !slider.cityId || String(slider.cityId) === String(cityId));

/** @typedef {"shown" | "hidden" | "all"} SliderStatusFilter */

/** The الحالة filter. The page opens on "shown": what the customer sees. */
export const SLIDER_STATUS = Object.freeze({ SHOWN: "shown", HIDDEN: "hidden", ALL: "all" });

/**
 * The sliders with one status, in their order.
 *
 * @param {Array<{id: number, isHidden?: boolean}>} sliders
 * @param {SliderStatusFilter} status
 * @returns {Array<object>}
 */
export const slidersOfStatus = (sliders, status) => {
    if (status === SLIDER_STATUS.SHOWN) return sliders.filter((slider) => !slider.isHidden);
    if (status === SLIDER_STATUS.HIDDEN)
        return sliders.filter((slider) => Boolean(slider.isHidden));
    return sliders;
};

/**
 * The full order after one slider moves one step in the list on screen.
 *
 * @param {number[]} allIds - every slider id, in the current order
 * @param {number[]} visibleIds - the ids on screen, in the current order
 * @param {number} id - the slider to move
 * @param {"up"|"down"} direction - "up" is toward the first place
 * @returns {number[]|null} the new full order, or null when it cannot move
 */
export const moveSlider = (allIds, visibleIds, id, direction) => {
    const place = visibleIds.indexOf(id);
    const neighbour = visibleIds[direction === "up" ? place - 1 : place + 1];
    if (place === -1 || neighbour === undefined) return null;

    const next = [...allIds];
    const from = next.indexOf(id);
    const to = next.indexOf(neighbour);
    if (from === -1 || to === -1) return null;
    [next[from], next[to]] = [next[to], next[from]];
    return next;
};
